<template>
  <div>
    <h2 class="font-20 font-weight-bold black2-color">Mortgage details</h2>
    <BaseLabel
      v-if="!isShowPropertyDetailsFields && !isEnteringAddressManually"
      class="mt-3 mb-2"
      text="Find your property"
      :helpText="
        $vuetify.breakpoint.lgAndUp
          ? 'Please provide the address of the property listed on the mortgage agreement to be covered by the policy.'
          : ''
      "
    ></BaseLabel>
    <BaseAddressLookup
      :rules="{
        required: {
          required: true,
          fieldLabel: 'Find your property',
        },
      }"
      :loading="false"
      :placeholder="
        $vuetify.breakpoint.lgAndUp
          ? 'Start typing your address or postcode...'
          : 'Start typing...'
      "
      data-uiid="InsuredAddress"
      manualFormUiid="InsuredAddress"
      id="address-lookup-insuredAddress"
      showManualAddressForm
      manualAddressFormText="Your address"
      :hideDetails="false"
      :address="insuredAddress"
      @address-selected="
        (response) => addressSelected(response, insuredAddress)
      "
      @use-address-finder="useAddressFinder"
      @enter-address-manually="isEnteringAddressManually = true"
      @is-complete-fill-address="onCompleteFillAddress"
      ref="autocomplete"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel text="Mortgage lender" class="mt-3 mb-2" />
    <BaseInput
      :vid="questionMortgageLender.InterfaceKey"
      v-model="questionMortgageLender.Value"
      :data-uiid="questionMortgageLender.InterfaceKey"
      :hideDetails="false"
      :rules="questionMortgageLenderRules"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel text="Mortgage completion date" class="mt-3 mb-2" />
    <BaseInputDate
      v-model="questionMortgageStartDate.Value"
      dataUiid="MortgageStartDate"
      :vid="questionMortgageStartDate.InterfaceKey"
      :rules="questionMortgageStartDateRules"
      :hide-details="false"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel text="Type of borrowing" class="mt-3 mb-2" />
    <BaseSelect
      dense
      v-model="questionTypeOfBorrowing.Value"
      :items="typeOfBorrowingItems"
      :data-uiid="questionTypeOfBorrowing.InterfaceKey"
      :hide-details="false"
      :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
      :rules="{
        required: {
          required: true,
          fieldLabel: 'Type of borrowing',
        },
      }"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel
      class="mt-3 mb-2"
      text="Does your mortgage benefit include a shared ownership payment (e.g. rental or equity loan payment)?"
      :helpText="
        $vuetify.breakpoint.lgAndUp
          ? 'Please indicate if the customer\'s benefit will include a shared ownership payment.'
          : ''
      "
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '540px' : '100%',
      }"
    />
    <BaseToggleButton
      v-model="questionSharedOwnership.Value"
      :data-uiid="questionSharedOwnership.InterfaceKey"
      :toggleButtonText="{
        yesDataUiid: `${questionSharedOwnership.InterfaceKey}_Yes`,
        noDataUiid: `${questionSharedOwnership.InterfaceKey}_No`,
      }"
      styleClass="mb-12"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
      :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
    />

    <BaseLabel
      class="mt-3 mb-2"
      :helpText="
        $vuetify.breakpoint.lgAndUp
          ? 'Please indicate if the customer\'s mortgage has a loan to value ratio (LTV) of 100% or more.'
          : ''
      "
      text="Is the mortgage a 100% plus advance mortgage?"
    />
    <BaseToggleButton
      v-model="questionAdvancedMortgage.Value"
      :data-uiid="questionAdvancedMortgage.InterfaceKey"
      :toggleButtonText="{
        yesDataUiid: `${questionAdvancedMortgage.InterfaceKey}_Yes`,
        noDataUiid: `${questionAdvancedMortgage.InterfaceKey}_No`,
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
      :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
    />
  </div>
</template>

<script>
import { addressSelected } from "@/common/addresses";
import { addFieldLabel, convertToBool } from "@/common/utils";
import { mapState } from "vuex";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import * as ERROR_MESSAGES from "@/constants/message.constants.js";
import { APPLICATION_MODULE } from "@/store/types/module-types";
import {
  TYPE_OF_BORROWING,
  setItemDataUiids,
} from "@/constants/component.constants.js";
import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import { ADDRESS_FINDER_INPUT_DESCRIPTION } from "@/constants/description.constants";
import { DATE_REGEX } from "@/constants/regex-patterns.constants";
import { ERROR_INVALID } from "@/constants/message.constants.js";

export default {
  components: {
    BaseLabel,
    BaseSelect,
    BaseInput,
    BaseInputDate,
    BaseToggleButton,
    BaseAddressLookup,
  },
  data() {
    return {
      insuredAddress: {},
      questionMortgageLender: {},
      questionMortgageStartDate: {},
      questionTypeOfBorrowing: {},
      questionSharedOwnership: {},
      questionAdvancedMortgage: {},
      questionPolicyStartDate: {},
      isEnteringAddressManually: false,
      isShowPropertyDetailsFields: false,
      LABEL_CONSTANTS,
      ERROR_MESSAGES,
      TYPE_OF_BORROWING,
      ADDRESS_FINDER_INPUT_DESCRIPTION,
      ERROR_INVALID,
      questionMortgageLenderRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 40,
            errorMessageTemplate: ERROR_MESSAGES.ERROR_MSG_MAX_CHARACTER,
          },
        },
        "Mortgage lender"
      ),
    };
  },
  watch: {},
  computed: {
    ...mapState(APPLICATION_MODULE, {
      applicationQuestionMap: "questionMap",
    }),

    typeOfBorrowingItems() {
      return setItemDataUiids("TypeOfBorrowing", TYPE_OF_BORROWING);
    },

    questionMortgageStartDateRules() {
      return {
        required: {
          fieldLabel: "Mortgage completion date",
        },
        date_valid: {
          fieldLabel: "Mortgage completion date",
          errorMessageTemplate: ERROR_MESSAGES.ERROR_INVALID,
        },
        regex: {
          fieldLabel: "Mortgage completion date",
          errorMessageTemplate: ERROR_MESSAGES.ERROR_INVALID,
          pattern: DATE_REGEX,
        },
        date_in_range: {
          min: 1,
          max: 1,
          units: "years",
          customMessage:
            "Mortgage completion date must be less than 1 year in the past and be no more than 1 year into the future.",
        },
        date_is_same_or_before: {
          compareValue: `@${this.questionPolicyStartDate.InterfaceKey}`,
          customMessage:
            "Policy start date must be the same as or later than the mortgage start date.",
        },

        date_is_same_or_after: {
          compareValue: `@${this.questionPolicyStartDate.InterfaceKey}`,
          durationValue: {
            days: -30,
          },
          customMessage:
            "Mortgage Protector is only available when applying for a new mortgage (including remortgaging) or if the applicant's mortgage agreement has been in force for 30 days or less.",
        },
      };
    },
  },
  beforeMount() {
    this.onSetupAndLoadSavedData();
    this.initAddressForm();
  },

  methods: {
    addressSelected,
    onSetupAndLoadSavedData() {
      this.questionMortgageLender =
        this.applicationQuestionMap["MortgageLender"];
      this.questionMortgageStartDate =
        this.applicationQuestionMap["MortgageStartDate"];
      this.questionTypeOfBorrowing =
        this.applicationQuestionMap["TypeOfBorrowing"];
      this.questionSharedOwnership =
        this.applicationQuestionMap["SharedOwnership"];
      this.questionAdvancedMortgage =
        this.applicationQuestionMap["AdvancedMortgage"];
      this.questionPolicyStartDate =
        this.applicationQuestionMap["PolicyStartDate"];

      this.questionSharedOwnership.Value = convertToBool(
        this.questionSharedOwnership.Value
      );
      this.questionAdvancedMortgage.Value = convertToBool(
        this.questionAdvancedMortgage.Value
      );
    },
    initAddressForm() {
      this.insuredAddress = {
        PostCode: this.applicationQuestionMap["InsuredAddressPostCode"],
        Street: this.applicationQuestionMap["InsuredAddressStreet"],
        District: this.applicationQuestionMap["InsuredAddressDistrict"],
        Town: this.applicationQuestionMap["InsuredAddressTown"],
        County: this.applicationQuestionMap["InsuredAddressCounty"],
      };

      if (this.insuredAddress.PostCode.Value) {
        this.isShowPropertyDetailsFields = true;
      }
    },
    getAddressSelected(response, address) {
      addressSelected(response, address);
    },
    onCompleteFillAddress(value) {
      this.isShowPropertyDetailsFields = value;
    },
    useAddressFinder() {
      this.isEnteringAddressManually = false;
    },
  },
};
</script>
